import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import moment from "moment";
import { getBancosCuentasActivos } from "./ApiServices";
import {
  buildAdelantoOrdenPagoBody,
  formatCuit,
  formatMoneda,
} from "./utils";

const cuitDeProv = (prov) =>
  String(prov?.nro_cuit || prov?.cuit || "").replace(/\D/g, "");

const etiquetaProveedor = (prov) => {
  const cuit = formatCuit(cuitDeProv(prov));
  const razon = prov?.razon_social || "Sin razón social";
  return `${razon} · ${cuit}`;
};

const claveBanco = (cuenta) => cuenta?.codigo ?? cuenta?.razon_social ?? "";

const idBancoDeCuenta = (cuenta) => {
  if (cuenta?.id_banco != null && cuenta.id_banco !== "") {
    const n = Number(cuenta.id_banco);
    if (Number.isFinite(n) && n > 0) return n;
  }
  const codigo = Number(cuenta?.codigo);
  if (Number.isFinite(codigo) && codigo > 0) return codigo;
  return null;
};

export default function ModalCrearAdelanto({
  open,
  onClose,
  onConfirm,
  proveedores = [],
  idEmpresa,
  generando = false,
}) {
  const [proveedorSel, setProveedorSel] = useState(null);
  const [cuitInput, setCuitInput] = useState("");
  const [monto, setMonto] = useState("");
  const [motivo, setMotivo] = useState("");
  const [codigoBanco, setCodigoBanco] = useState("");
  const [idBancosCuentas, setIdBancosCuentas] = useState("");
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  const [loadingCuentas, setLoadingCuentas] = useState(false);
  const [errorForm, setErrorForm] = useState("");

  const opciones = useMemo(() => {
    const map = new Map();
    (proveedores || []).forEach((prov) => {
      const cuit = cuitDeProv(prov);
      if (!cuit || map.has(cuit)) return;
      map.set(cuit, { ...prov, _cuit: cuit });
    });
    return Array.from(map.values());
  }, [proveedores]);

  const bancosUnicos = useMemo(() => {
    const map = new Map();
    (cuentasBancarias || []).forEach((c) => {
      const key = claveBanco(c);
      if (!key || map.has(key)) return;
      map.set(key, { codigo: key, nombre: c.razon_social || c.codigo || key });
    });
    return Array.from(map.values());
  }, [cuentasBancarias]);

  const cuentasDelBanco = useMemo(() => {
    if (!codigoBanco) return [];
    return (cuentasBancarias || []).filter(
      (c) => claveBanco(c) === codigoBanco
    );
  }, [cuentasBancarias, codigoBanco]);

  const cuentaSeleccionada = useMemo(
    () =>
      cuentasDelBanco.find(
        (c) => String(c.id_bancos_cuentas) === String(idBancosCuentas)
      ) ?? null,
    [cuentasDelBanco, idBancosCuentas]
  );

  const montoNum = Number(String(monto).replace(",", "."));
  const montoValido = Number.isFinite(montoNum) && montoNum > 0;

  useEffect(() => {
    if (!open) {
      setProveedorSel(null);
      setCuitInput("");
      setMonto("");
      setMotivo("");
      setCodigoBanco("");
      setIdBancosCuentas("");
      setErrorForm("");
      return;
    }

    let activo = true;
    setLoadingCuentas(true);
    getBancosCuentasActivos()
      .then((data) => {
        if (activo) setCuentasBancarias(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (activo) setCuentasBancarias([]);
      })
      .finally(() => {
        if (activo) setLoadingCuentas(false);
      });

    return () => {
      activo = false;
    };
  }, [open]);

  const handleConfirmar = () => {
    setErrorForm("");

    if (!proveedorSel?._cuit) {
      setErrorForm("Seleccioná un proveedor del listado");
      return;
    }
    if (!montoValido) {
      setErrorForm("Ingresá un monto mayor a 0");
      return;
    }
    const motivoTrim = String(motivo || "").trim();
    if (!motivoTrim) {
      setErrorForm("Ingresá el motivo del adelanto");
      return;
    }
    if (!codigoBanco) {
      setErrorForm("Seleccioná el banco de origen");
      return;
    }
    if (!idBancosCuentas) {
      setErrorForm("Seleccioná la cuenta de origen");
      return;
    }

    const idBanco = idBancoDeCuenta(cuentaSeleccionada);
    if (!idBanco) {
      setErrorForm("No se pudo determinar el banco de la cuenta seleccionada");
      return;
    }

    try {
      const body = buildAdelantoOrdenPagoBody({
        id_empresa: idEmpresa,
        monto: montoNum,
        motivo: motivoTrim,
        fechaPago: moment().format("YYYY-MM-DD"),
        user: localStorage.getItem("user"),
        id_banco: idBanco,
        id_bancos_cuentas: idBancosCuentas,
      });
      onConfirm?.(proveedorSel._cuit, body);
    } catch (err) {
      setErrorForm(err.message || "No se pudo armar el adelanto");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={generando ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 700 }}>Crear adelanto / anticipo</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Genera una orden de pago con concepto adelanto. Quedará como saldo a
          favor del proveedor; todavía no se puede liquidar contra facturas.
        </Typography>

        {opciones.length === 0 ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No hay proveedores en el listado actual. Ajustá los filtros para
            poder buscar por CUIT.
          </Alert>
        ) : null}

        {errorForm ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorForm}
          </Alert>
        ) : null}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Autocomplete
              options={opciones}
              value={proveedorSel}
              onChange={(_, value) => setProveedorSel(value)}
              inputValue={cuitInput}
              onInputChange={(_, value) => setCuitInput(value)}
              getOptionLabel={etiquetaProveedor}
              isOptionEqualToValue={(a, b) => a?._cuit === b?._cuit}
              filterOptions={(opts, state) => {
                const qTexto = String(state.inputValue || "").trim().toLowerCase();
                if (!qTexto) return opts;
                const qCuit = qTexto.replace(/\D/g, "");
                return opts.filter((o) => {
                  const cuit = String(o._cuit || "");
                  const razon = String(o.razon_social || "").toLowerCase();
                  const nombre = String(
                    o.nombre || o.nombre_fantasia || o.nombre_proveedor || ""
                  ).toLowerCase();
                  const matchNombre =
                    razon.includes(qTexto) || nombre.includes(qTexto);
                  const matchCuit =
                    qCuit.length > 0 &&
                    (cuit.includes(qCuit) ||
                      formatCuit(cuit).toLowerCase().includes(qTexto));
                  return matchNombre || matchCuit;
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Buscar proveedor (nombre / CUIT)"
                  placeholder="Escribí nombre o CUIT"
                  size="small"
                />
              )}
              disabled={generando || opciones.length === 0}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Monto"
              type="number"
              value={monto}
              onChange={(e) => setMonto(e.target.value)}
              inputProps={{ min: 0.01, step: 0.01 }}
              helperText={montoValido ? formatMoneda(montoNum) : " "}
              disabled={generando}
            />
          </Grid>

          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              size="small"
              label="Motivo"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value.slice(0, 250))}
              helperText={`${String(motivo || "").length} / 250`}
              disabled={generando}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small" disabled={generando || loadingCuentas}>
              <InputLabel>Banco origen</InputLabel>
              <Select
                label="Banco origen"
                value={codigoBanco}
                onChange={(e) => {
                  setCodigoBanco(e.target.value);
                  setIdBancosCuentas("");
                }}
              >
                {bancosUnicos.map((b) => (
                  <MenuItem key={b.codigo} value={b.codigo}>
                    {b.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl
              fullWidth
              size="small"
              disabled={generando || loadingCuentas || !codigoBanco}
            >
              <InputLabel>Cuenta origen</InputLabel>
              <Select
                label="Cuenta origen"
                value={idBancosCuentas}
                onChange={(e) => setIdBancosCuentas(e.target.value)}
              >
                {cuentasDelBanco.map((c) => (
                  <MenuItem
                    key={c.id_bancos_cuentas}
                    value={c.id_bancos_cuentas}
                  >
                    {c.nro_cuenta
                      ? `${c.nro_cuenta}${c.cbu ? ` — ${c.cbu}` : ""}`
                      : c.razon_social}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {loadingCuentas ? (
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="caption" color="text.secondary">
                  Cargando bancos y cuentas…
                </Typography>
              </Box>
            </Grid>
          ) : null}

          {!loadingCuentas && cuentasBancarias.length === 0 ? (
            <Grid item xs={12}>
              <Alert severity="warning">
                No hay cuentas bancarias activas para seleccionar.
              </Alert>
            </Grid>
          ) : null}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={generando}>
          Cancelar
        </Button>
        <Box sx={{ position: "relative" }}>
          <Button
            variant="contained"
            onClick={handleConfirmar}
            disabled={
              generando ||
              opciones.length === 0 ||
              loadingCuentas ||
              cuentasBancarias.length === 0
            }
          >
            Generar adelanto
          </Button>
          {generando ? (
            <CircularProgress
              size={24}
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                mt: "-12px",
                ml: "-12px",
              }}
            />
          ) : null}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
